import type { Reducer } from '@reduxjs/toolkit';
import type { ResetError } from './ApplicationActions';
import { resetReducerErrors, getReducerError, type ReducerError } from '../Utils/reducerError';
import createUuid from '../Utils/createUuid';

export type LoadingState = {
  login: boolean;
  signup: boolean;
  register: boolean;
  activateSMS: boolean;
  applicationData: boolean;
  chatrooms: boolean;
  contacts: boolean;
  search: boolean;
  media: boolean;
  profilePicture: boolean;
  updateProfile: boolean;
  updateEmail: boolean;
  cancelUpdateEmail: boolean;
  updatePhone: boolean;
  offers: boolean;
  organizations: boolean;
  surgeons: boolean;
  joinOrganization: boolean;
  cases: boolean;
  donor: boolean;
  followers: boolean;
  follower: boolean;
  followerTasks: boolean;
  followerPeople: boolean;
  tasks: boolean;
  donorTask: boolean;
  taskSaving: boolean;
  taskData: boolean;
  orgMembers: boolean;
  teams: boolean;
  saveTeam: boolean;
  updateDonor: boolean;
  saveDonor: boolean;
  importDonorData: boolean;
  permissions: boolean;
  notifiableEventGroup: boolean;
  users: boolean;
  caseTimeline: boolean;
  generatePDF: boolean;
  recipientUpload: boolean;
  roomsList: boolean;
  updateAlert: boolean;
  resetExpiredPassword: boolean;

  taskStatus: { [key: string]: boolean };

  // Loading of these are room specific
  chatFiles: { [key: string]: boolean };
  donorFiles: { [key: string]: boolean };
  chatMembers: { [key: string]: boolean };
  chatMessages: { [key: string]: boolean };
  pinnedMessages: { [key: string]: boolean };

  // Each key is `${chatId}-${msgId}`
  chatMessageStatuses: { [key: string]: boolean };
  reducerErrors: Array<ReducerError>;
};

export type Identifier =
  | 'login'
  | 'signup'
  | 'register'
  | 'activateSMS'
  | 'applicationData'
  | 'chatrooms'
  | 'contacts'
  | 'search'
  | 'media'
  | 'saveChat'
  | 'profilePicture'
  | 'updateProfile'
  | 'updateEmail'
  | 'cancelUpdateEmail'
  | 'updatePhone'
  | 'offers'
  | 'organizations'
  | 'surgeons'
  | 'joinOrganization'
  | 'cases'
  | 'donor'
  | 'followers'
  | 'follower'
  | 'followerTasks'
  | 'followerPeople'
  | 'tasks'
  | 'donorTask'
  | 'taskSaving'
  | 'taskData'
  | 'orgMembers'
  | 'teams'
  | 'donorFiles'
  | 'updateDonor'
  | 'updateAlert'
  | 'createServiceInfoRoom'
  | 'saveDonor'
  | 'importDonorData'
  | 'permissions'
  | 'notifiableEventGroup'
  | 'users'
  | 'saveTeam'
  | 'deleteTeam'
  | 'caseTimeline'
  | 'generatePDF'
  | 'recipientUpload'
  | 'roomsList'
  | 'resetExpiredPassword';

export type EntityIdentifier =
  | 'chatFiles'
  | 'chatMembers'
  | 'chatMessages'
  | 'chatMessageStatuses'
  | 'donorFiles'
  | 'taskStatus'
  | 'pinnedMessages';

const initialState: LoadingState = {
  login: false,
  signup: false,
  register: false,
  activateSMS: false,
  applicationData: false,
  chatrooms: false,
  contacts: false,
  search: false,
  media: false,
  profilePicture: false,
  updateProfile: false,
  updateEmail: false,
  cancelUpdateEmail: false,
  updatePhone: false,
  offers: false,
  cases: false,
  donor: false,
  followers: false,
  follower: false,
  followerTasks: false,
  followerPeople: false,
  tasks: false,
  donorTask: false,
  taskSaving: false,
  taskData: false,
  orgMembers: false,
  teams: false,
  saveTeam: false,
  updateDonor: false,
  updateAlert: false,
  saveDonor: false,
  importDonorData: false,
  organizations: false,
  surgeons: false,
  joinOrganization: false,
  permissions: false,
  notifiableEventGroup: false,
  users: false,
  caseTimeline: false,
  generatePDF: false,
  recipientUpload: false,
  roomsList: false,
  resetExpiredPassword: false,
  reducerErrors: [],
  taskStatus: {},
  chatFiles: {},
  donorFiles: {},
  chatMembers: {},
  chatMessages: {},
  pinnedMessages: {},
  chatMessageStatuses: {},
};

export type StartLoading = { type: 'Loading/START_LOADING'; identifier: Identifier };
export type FinishLoading = { type: 'Loading/FINISH_LOADING'; identifier: Identifier };
export type StartLoadingEntity = { type: 'Loading/START_LOADING_ENTITY'; identifier: EntityIdentifier; id: string };
export type FinishLoadingEntity = { type: 'Loading/FINISH_LOADING_ENTITY'; identifier: EntityIdentifier; id: string };

export type LoadingAction = StartLoading | FinishLoading | StartLoadingEntity | FinishLoadingEntity | ResetError;

export const startLoading = (identifier: Identifier): StartLoading => ({
  type: 'Loading/START_LOADING',
  identifier,
});

export const finishLoading = (identifier: Identifier): FinishLoading => ({
  type: 'Loading/FINISH_LOADING',
  identifier,
});

export const startLoadingEntity = (identifier: EntityIdentifier, id: number | string): StartLoadingEntity => ({
  type: 'Loading/START_LOADING_ENTITY',
  identifier,
  id: `${id}`,
});

export const finishLoadingEntity = (identifier: EntityIdentifier, id: number | string): FinishLoadingEntity => ({
  type: 'Loading/FINISH_LOADING_ENTITY',
  identifier,
  id: `${id}`,
});

const reducer = (state: LoadingState = initialState, action: LoadingAction): LoadingState => {
  // wrap the whole reducer to catch any unexpected exceptions and record them
  // Another component will need to act upon these and potentially send them to Sentry
  try {
    return innerReducer(state, action);
  } catch (err) {
    const reducerErrors = state.reducerErrors.slice();
    const uuid = createUuid(0);
    if (err instanceof Error) {
      reducerErrors.push(getReducerError(uuid, 'loading', action, err));
    } else {
      const unknownError = new Error('An unknown error occurred in LoadingActions');
      reducerErrors.push(getReducerError(uuid, 'loading', action, unknownError));
    }
    return {
      ...state,

      reducerErrors,
    };
  }
};

const innerReducer = (state: LoadingState = initialState, action: LoadingAction): LoadingState => {
  switch (action.type) {
    case 'Application/RESET_ERROR': {
      const reducerErrors = resetReducerErrors(state.reducerErrors, action);
      return {
        ...state,
        reducerErrors,
      };
    }

    case 'Loading/START_LOADING':
      return {
        ...state,

        // $FlowFixMe
        [action.identifier]: true,
      };

    case 'Loading/FINISH_LOADING':
      return {
        ...state,

        // $FlowFixMe
        [action.identifier]: false,
      };
    case 'Loading/START_LOADING_ENTITY':
      return {
        ...state,

        // $FlowFixMe
        [action.identifier]: {
          ...state[action.identifier],

          [action.id]: true,
        },
      };

    case 'Loading/FINISH_LOADING_ENTITY':
      return {
        ...state,

        // $FlowFixMe
        [action.identifier]: {
          ...state[action.identifier],

          [action.id]: false,
        },
      };
    default:
      return state;
  }
};

export default reducer as Reducer<LoadingState>;
